/**
 * A nova organização com contrato como a API pede (#769), sem tela.
 *
 * `POST /v1/operacao/revendas` aceita os campos de antes e os novos; a tela
 * manda sempre os novos: o cadastro, o fuso, o contrato do catálogo e o contato
 * financeiro. O plano em texto não vai mais: quem diz o plano é o catálogo.
 */

import {
  SegmentoDaOrganizacaoSchema,
  type Plano,
  type ProvisionarRevenda,
} from '@cliqcrm/contracts';
import { emMicros } from '@/shared/lib/formato';
import {
  valoresDoPlano,
  type Dinheiro,
  type NovaOrganizacaoComContrato,
  type PlanoParaEscolher,
} from './formulario-com-contrato';

/**
 * Os planos que a criação aceita: os ativos com os números. O "Padrão" das
 * organizações de antes do catálogo não tem preço, e a API o recusaria.
 */
export function planosParaEscolher(planos: readonly Plano[]): PlanoParaEscolher[] {
  return planos.flatMap((plano) =>
    plano.ativo &&
    plano.usuarios_incluidos !== null &&
    plano.mensalidade !== null &&
    plano.usuario_adicional !== null &&
    plano.gb_adicional !== null
      ? [
          {
            id: plano.id,
            nome: plano.nome,
            mensalidade: plano.mensalidade,
            usuariosIncluidos: plano.usuarios_incluidos,
            armazenamentoGb: plano.armazenamento_gb,
            usuarioAdicional: plano.usuario_adicional,
            gbAdicional: plano.gb_adicional,
          },
        ]
      : [],
  );
}

/**
 * O valor deste cliente, em reais como a API aceita, só quando a pessoa o mudou.
 * A comparação é com o valor do plano como o formulário o mostrou (duas casas),
 * e não com o do catálogo: um plano de "2,995" aparece como "2,99", e mandar
 * esse número faria o contrato nascer "Personalizado" sem ninguém ter mexido.
 * Sem o valor, a API usa o do plano.
 */
function seMudou(valor: Dinheiro | null, doPlanoNaTela: string | undefined) {
  if (valor === null) return undefined;
  if (doPlanoNaTela !== undefined && emMicros(doPlanoNaTela) === valor.quantia_micros) {
    return undefined;
  }
  return { quantia_micros: valor.quantia_micros, moeda: 'BRL' as const };
}

/** As perguntas do servidor que a pessoa já viu, e que o próximo envio confirma. */
export type ConfirmacoesDaCriacao = {
  /** O e-mail do administrador já tem conta em outra organização. */
  readonly contaExistente: boolean;
  /** O identificador é de uma organização encerrada, que a criação continua (#1118). */
  readonly retomadaDeEncerrada: boolean;
};

/**
 * O corpo da criação. `plano` é o escolhido, para o valor que a pessoa não mudou
 * ficar o do plano. `origem` é o endereço desta instalação, para onde o login
 * volta. `confirmacoes` são as perguntas que a pessoa já viu na tela.
 */
export function corpoDaCriacao(
  dados: NovaOrganizacaoComContrato,
  plano: PlanoParaEscolher | undefined,
  origem: string,
  confirmacoes: ConfirmacoesDaCriacao,
): ProvisionarRevenda {
  const naTela = plano ? valoresDoPlano(plano) : undefined;
  const usuarioAdicional = seMudou(dados.usuarioAdicional, naTela?.adicional);
  const gbAdicional = seMudou(dados.gbAdicional, naTela?.gbAdicional);
  return {
    slug: dados.identificador,
    nome: dados.nomeFantasia,
    admin: {
      nome: dados.administrador.nome,
      email: dados.administrador.email,
      ...(dados.administrador.telefone ? { telefone: dados.administrador.telefone } : {}),
    },
    origensWeb: [origem],
    cadastro: {
      razao_social: dados.razaoSocial,
      cnpj: dados.cnpj,
      // A escolha só oferece os segmentos do contrato.
      segmento: SegmentoDaOrganizacaoSchema.parse(dados.segmento),
    },
    fuso: dados.fuso,
    contrato: {
      plano_id: dados.planoId,
      inicio: dados.inicioDoContrato,
      usuarios_contratados: dados.usuariosContratados,
      armazenamento_gb: dados.armazenamentoContratadoGb,
      ...(usuarioAdicional ? { usuario_adicional: usuarioAdicional } : {}),
      ...(gbAdicional ? { gb_adicional: gbAdicional } : {}),
    },
    financeiro: dados.financeiro,
    ...(confirmacoes.contaExistente ? { confirmarContaExistente: true } : {}),
    ...(confirmacoes.retomadaDeEncerrada ? { confirmarRetomadaDeEncerrada: true } : {}),
  };
}

/** O dia de hoje no relógio de quem usa a tela, AAAA-MM-DD: o início padrão do contrato. */
export function hojeLocal(agora = new Date()): string {
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}
